// components/features/tickets/TicketRow.tsx

import { Ticket } from "../../../types/features/Ticket";
import TableRow from "../../ui/TableRow";

interface TicketRowProps {
  ticket: Ticket;
  onAction: (ticket: Ticket) => void;
  onSelect?: (ticket: Ticket, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function TicketRow({
  ticket,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false,
}: TicketRowProps) {
  const getPriorityLabel = () => {
    switch (ticket.priority) {
      case "critique":
        return { label: "Critique", className: "bg-red-100 text-red-700" };
      case "haute":
        return { label: "Haute", className: "bg-orange-100 text-orange-700" };
      case "moyenne":
        return { label: "Moyenne", className: "bg-yellow-100 text-yellow-700" };
      case "basse":
        return { label: "Basse", className: "bg-green-100 text-green-700" };
      default:
        return { label: "Moyenne", className: "bg-gray-100 text-gray-700" };
    }
  };

  const getStatusLabel = () => {
    switch (ticket.status) {
      case "nouveau":
        return { label: "Nouveau", className: "bg-blue-100 text-blue-700" };
      case "en_cours":
        return { label: "En cours", className: "bg-purple-100 text-purple-700" };
      case "en_attente":
        return { label: "En attente", className: "bg-yellow-100 text-yellow-700" };
      case "escalade":
        return { label: "Escalade", className: "bg-red-100 text-red-700" };
      case "resolu":
        return { label: "Résolu", className: "bg-green-100 text-green-700" };
      case "ferme":
        return { label: "Fermé", className: "bg-gray-100 text-gray-700" };
      default:
        return { label: "Nouveau", className: "bg-gray-100 text-gray-700" };
    }
  };

  const priority = getPriorityLabel();
  const status = getStatusLabel();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Vérifier si le ticket est dans la file d'attente
  const isInQueue = ["nouveau", "en_cours", "en_attente"].includes(ticket.status);

  return (
    <TableRow
      item={ticket}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">{ticket.client?.full_name}</span>
          {/* Position : affichée uniquement si le ticket est dans la file */}
          {isInQueue && ticket.position !== null && (ticket?.position || 0) > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
              #{ticket.position}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{ticket.title}</p>
      </td>

      <td className="hidden sm:table-cell py-3 px-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${priority.className}`}>{priority.label}</span>
      </td>

      <td className="hidden md:table-cell py-3 px-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
      </td>

      <td className="hidden lg:table-cell py-3 px-4">
        <span className="text-gray-600 text-sm">
          {ticket.source === "appel" && "Appel"}
          {ticket.source === "formulaire_web" && "Formulaire web"}
          {ticket.source === "email" && "Email"}
          {ticket.source === "api" && "API"}
          {ticket.source === "dashboard" && "Tableau de bord"}

        </span>
      </td>

      <td className="hidden xl:table-cell py-3 px-4">
        <span className="text-gray-500 text-xs">{formatDate(ticket.created_at)}</span>
      </td>
    </TableRow>
  );
}